import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { DropdownMenu } from '../DropdownMenu';
import { Button, ButtonProps } from '../Button';
import { useDateFNSConfig } from '@nexelem/config';
import { format, set } from 'date-fns';
import { cn } from '@nexelem/utils';
import { faCalendarDay } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const DEFAULT_YEAR_RANGE = 10;

type CalendarMonthYearInputProps = {
  selected: Date;
  onSelect?: (selected: Date) => void;
  variant?: ButtonProps['variant'];
  minYear?: number;
  maxYear?: number;
};

export const CalendarMonthYearInput = ({
  selected,
  onSelect,
  variant,
  minYear,
  maxYear,
}: CalendarMonthYearInputProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { locale } = useDateFNSConfig();
  const activeRef = useRef<HTMLButtonElement>(null);

  const selectedYear = selected.getFullYear();

  const monthNames = getMonthNames(locale);
  const years = getYears(minYear, maxYear);

  const scrollToActive = (behavior: ScrollBehavior = 'auto') => {
    // Small timeout ensures the DOM has painted the open menu
    setTimeout(() => {
      activeRef.current?.scrollIntoView({
        block: 'center',
        behavior,
      });
    }, 0);
  };

  // scroll to the active year on mount
  useLayoutEffect(() => {
    isOpen && scrollToActive('auto');
  }, [isOpen]);

  // scroll to the active year on change
  useEffect(() => {
    isOpen && scrollToActive('smooth');
  }, [selectedYear, isOpen]);

  const handleMonthSelect = (month: number) => {
    onSelect?.(set(new Date(selected), { month }));
  };

  const handleYearSelect = (year: number) => {
    onSelect?.(set(new Date(selected), { year }));
  };

  return (
    <DropdownMenu.Root onOpenChange={setIsOpen}>
      <DropdownMenu.Trigger>
        <Button className="capitalize" StartIcon={<FontAwesomeIcon icon={faCalendarDay} />} variant={variant}>
          {format(selected, 'LLLL yyyy', { locale })}
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content align="start" className="z-5002 mt-2 flex flex-row gap-2 px-4 py-3">
        <div className="grid grid-cols-3 gap-1 py-1">
          {monthNames?.map((monthName) => {
            const month = monthNames.indexOf(monthName);
            const isActive = selected.getMonth() === month;
            return (
              <Button
                key={monthName}
                variant="text"
                className={cn(
                  'h-9 w-20 rounded-full text-s16 capitalize',
                  isActive && 'border border-border-brand! bg-surface-focus-lighter text-text-brand!',
                )}
                onClick={() => handleMonthSelect(monthNames.indexOf(monthName))}
              >
                {monthName}
              </Button>
            );
          })}
        </div>
        <div className="flex max-h-40 flex-col gap-1 overflow-auto rounded-sm border border-border-container bg-surface-container p-1">
          {years?.map((year) => {
            const isActive = selected.getFullYear() === year;

            return (
              <Button
                key={year}
                ref={isActive ? activeRef : null}
                type="button"
                variant="text"
                className={cn(
                  'h-9 w-20 shrink-0 rounded-full capitalize',
                  isActive && 'border border-border-brand! bg-surface-focus-lighter text-text-brand!',
                )}
                onClick={() => handleYearSelect(year)}
              >
                {year}
              </Button>
            );
          })}
        </div>
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
};

const getMonthNames = (locale: Locale) =>
  Array.from({ length: 12 }, (_, i) => {
    const date = new Date(2024, i, 1);
    return format(date, 'MMM', { locale });
  });

const getYears = (from?: number, to?: number) => {
  const currentYear = new Date().getFullYear();
  const fromYear = from ?? currentYear - DEFAULT_YEAR_RANGE;
  const toYear = to ?? currentYear + DEFAULT_YEAR_RANGE;

  return Array.from({ length: toYear - fromYear + 1 }, (_, i) => fromYear + i);
};
